/* TF Cart Drawer — slide-in cart panel */
(function () {
  'use strict';

  /* Shared client-side money formatter. Mirrors Shopify's
     money_without_trailing_zeros for the store's money_format, so optimistic
     prices match the server-rendered ones exactly. Exposed for the cart page. */
  function tfMoney(cents) {
    var fmt = window.TFmoneyFormat || 'Rs.{{amount}}';
    cents = Math.round(cents);
    function grp(s, sep) { return s.replace(/\B(?=(\d{3})+(?!\d))/g, sep); }
    function build(decimals, thousands, dec) {
      var neg = cents < 0, c = Math.abs(cents);
      var s = (c / 100).toFixed(decimals).split('.');
      s[0] = grp(s[0], thousands);
      return (neg ? '-' : '') + (decimals > 0 ? s[0] + dec + s[1] : s[0]);
    }
    function noTrail(thousands, dec) {
      return (Math.abs(cents) % 100 === 0) ? build(0, thousands, dec) : build(2, thousands, dec);
    }
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_, n) {
      switch (n) {
        case 'amount_no_decimals': return build(0, ',', '.');
        case 'amount_with_comma_separator': return noTrail('.', ',');
        case 'amount_no_decimals_with_comma_separator': return build(0, '.', ',');
        case 'amount_with_apostrophe_separator': return noTrail("'", '.');
        default: return noTrail(',', '.'); // {{amount}}
      }
    });
  }
  window.tfMoney = tfMoney;

  class TFCartDrawer {
    constructor() {
      this.drawer   = document.getElementById('tf-sidecart');
      this.backdrop = document.getElementById('tf-sc-backdrop');
      this.sectionId = window.TFCartDrawerSectionId || 'tf-cart-drawer';
      if (!this.drawer) return;
      this._bindAll();
    }

    /* ---------- binding ---------- */
    _bindAll() {
      // Open: cart icon in header
      document.addEventListener('click', (e) => {
        if (e.target.closest('[data-open-tf-cart]')) { e.preventDefault(); this.open(); }
        // Only prevent default for non-link close triggers (buttons/divs); let <a> links navigate
        const closeEl = e.target.closest('[data-tf-cart-close]');
        if (closeEl && !this.drawer.contains(closeEl)) {
          if (closeEl.tagName !== 'A') e.preventDefault();
          this.close();
        }
      });

      // Delegate all interactions inside drawer
      this.drawer.addEventListener('click', (e) => {
        const close   = e.target.closest('[data-tf-cart-close]');
        const qtyBtn  = e.target.closest('.tf-sc-qty-btn');
        const rmBtn   = e.target.closest('.tf-sc-remove');
        const upsell  = e.target.closest('.tf-sc-upsell-add');

        if (close) {
          // Let <a> tags with real hrefs navigate; just close the drawer
          if (close.tagName !== 'A' || !close.getAttribute('href')) e.preventDefault();
          this.close();
        }
        if (qtyBtn) { e.preventDefault(); this._handleQty(qtyBtn); }
        if (rmBtn)  { e.preventDefault(); this._handleRemove(rmBtn); }
        if (upsell) { e.preventDefault(); this._handleUpsell(upsell); }
      });

      // Backdrop
      this.backdrop?.addEventListener('click', () => this.close());

      // Esc key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen()) this.close();
      });

      // Any add-to-cart success → open drawer
      document.addEventListener('tf:cart:open', () => this.open());

      // Intercept cart-notification open so popup is replaced by drawer
      this._patchCartNotification();
    }

    _patchCartNotification() {
      const cn = document.querySelector('cart-notification');
      if (!cn) return;

      // product-form.js asks the cart-notification which sections to fetch with
      // the /cart/add.js request. Point it at the drawer + badge so the ADD
      // response already contains everything we need — no second round trip,
      // and the unused notification-popup sections are never rendered.
      cn.getSectionsToRender = () => this._sections().map(s => ({
        id: s.sectionId,
        selector: s.selector,
      }));

      // Apply those sections straight from the add response and open the drawer
      // instead of showing the cart-notification popup.
      cn.renderContents = (parsedState) => {
        this._applySections(parsedState);
        this.open();
      };
    }

    /* ---------- open / close ---------- */
    isOpen()  { return this.drawer.classList.contains('open'); }

    open() {
      this.drawer.classList.add('open');
      this.backdrop?.classList.add('open');
      document.body.classList.add('tf-cart-open');
    }

    close() {
      this.drawer.classList.remove('open');
      this.backdrop?.classList.remove('open');
      document.body.classList.remove('tf-cart-open');
    }

    /* ---------- section rendering ---------- */
    _sections() {
      return [
        { domId: 'tf-sc-content',   sectionId: this.sectionId,    selector: '#tf-sc-content' },
        { domId: 'cart-icon-bubble', sectionId: 'cart-icon-bubble', selector: '.shopify-section' },
      ];
    }

    _applySections(parsed) {
      this._sections().forEach(({ domId, sectionId, selector }) => {
        const el  = document.getElementById(domId);
        const raw = parsed.sections?.[sectionId];
        if (!el || !raw) return;
        const doc    = new DOMParser().parseFromString(raw, 'text/html');
        const target = doc.querySelector(selector);
        if (!target) return;
        const dest = el.querySelector(selector) || el;
        dest.innerHTML = target.innerHTML;
      });
    }

    /* ---------- cart ops ---------- */
    _setLoading(on) {
      document.getElementById('tf-sc-content')?.classList.toggle('tf-sc--loading', on);
    }

    /* Change a line by its stable line-item KEY (never by index, which shifts
       as items are removed). All calls are serialized through one chain, and we
       only re-render once the queue is idle — so a slow earlier response can't
       resurrect an item a later click already removed. */
    _change(key, quantity) {
      if (!key) return;
      this._pending = (this._pending || 0) + 1;
      const run = () => fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({
          id: key,
          quantity: quantity,
          sections: this._sections().map(s => s.sectionId),
          sections_url: window.location.pathname,
        }),
      })
        .then(r => r.json())
        .then(state => { this._lastState = state; })
        .catch(() => {})
        .finally(() => {
          this._pending--;
          if (this._pending === 0 && this._lastState) {
            const scrollTop = this.drawer.querySelector('.sc-body')?.scrollTop || 0;
            this._applySections(this._lastState);
            this.drawer.querySelector('.sc-body')?.scrollTo(0, scrollTop);
            this._lastState = null;
          }
        });
      this._chain = (this._chain || Promise.resolve()).then(run, run);
    }

    /* Recompute line prices, subtotal, item count and free-ship bar straight
       from the DOM — instant, no server wait. The change.js response later
       reconciles the authoritative values. */
    _repaint() {
      const threshold = window.TFcartFreeShip || 250000;
      let subtotal = 0, count = 0;
      this.drawer.querySelectorAll('.sc-item').forEach((item) => {
        if (item.classList.contains('sc-item--removing')) return;
        const unit  = parseInt(item.dataset.unitPrice, 10) || 0;
        const qtyEl = item.querySelector('.sc-qty');
        const qty   = parseInt(qtyEl && qtyEl.dataset.qty, 10) || 0;
        const lp    = unit * qty;
        subtotal += lp; count += qty;
        const priceEl = item.querySelector('.sc-item-price');
        if (priceEl) priceEl.textContent = tfMoney(lp);
      });
      const countEl = this.drawer.querySelector('.sc-count');
      if (countEl) countEl.textContent = count + ' ' + (count === 1 ? 'item' : 'items');
      const subEl = this.drawer.querySelector('.sc-foot-price');
      if (subEl) subEl.textContent = tfMoney(subtotal);
      const fill = this.drawer.querySelector('.sc-bar-fill');
      if (fill) fill.style.width = (threshold ? Math.min(100, Math.round(subtotal * 100 / threshold)) : 100) + '%';
    }

    // Coalesce rapid +/- clicks on the same line into one server request.
    _debounceLine(key, getQty) {
      this._timers = this._timers || {};
      clearTimeout(this._timers[key]);
      this._timers[key] = setTimeout(() => this._change(key, getQty()), 350);
    }

    _fadeOut(item) {
      if (!item) return;
      item.classList.add('sc-item--removing');
      item.style.transition = 'opacity .15s, transform .15s';
      item.style.opacity = '0';
      item.style.transform = 'translateX(24px)';
    }

    _handleQty(btn) {
      const item  = btn.closest('.sc-item');
      const key   = item && item.dataset.key;
      const delta = parseInt(btn.dataset.delta, 10);
      const qtyEl = btn.closest('[data-qty]') || btn.closest('.sc-qty');
      const cur   = parseInt(qtyEl?.dataset.qty || qtyEl?.querySelector('span')?.textContent || 1, 10);
      const next  = Math.max(0, cur + delta);

      // Dropping to zero behaves like remove.
      if (next <= 0) {
        this._fadeOut(item);
        if (this._timers && key) clearTimeout(this._timers[key]);
        this._repaint();
        this._change(key, 0);
        return;
      }

      // Optimistic: qty + line price + subtotal + bar update instantly.
      if (qtyEl) {
        const span = qtyEl.querySelector('span');
        if (span) span.textContent = next;
        qtyEl.dataset.qty = next;
      }
      this._repaint();
      this._debounceLine(key, () => parseInt(qtyEl.dataset.qty, 10) || 0);
    }

    _handleRemove(btn) {
      const item = btn.closest('.sc-item');
      const key  = item && item.dataset.key;
      this._fadeOut(item);
      // Subtotal/count drop instantly; serialized change.js reconciles once idle.
      this._repaint();
      this._change(key, 0);
    }

    _handleUpsell(btn) {
      const variantId = parseInt(btn.dataset.variantId, 10);
      if (!variantId) return;
      btn.disabled = true;
      btn.textContent = '…';

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({
          id: variantId,
          quantity: 1,
          sections: this._sections().map(s => s.sectionId),
        }),
      })
        .then(r => r.json())
        .then(state => this._applySections(state))
        .catch(() => { btn.disabled = false; btn.textContent = '+ Add'; });
    }
  }

  /* Initialise once DOM is ready */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => { window.tfCartDrawer = new TFCartDrawer(); });
  } else {
    window.tfCartDrawer = new TFCartDrawer();
  }
})();
